import { ProfileHandling } from "services";
import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import { remove } from "aws-amplify/storage";
import outputs from "amplify_outputs.json";
import type { Schema } from "schema/data/resource";
import { generateClient } from "aws-amplify/data";
import { DocumentMetadataSanitizer, listAll } from "services";
import { getDocumentTypeLabel } from "types";

Amplify.configure(outputs);

const client = generateClient<Schema>({
  authMode: "userPool",
});

class DocumentService {
  private async getAuthInfo() {
    const session = await fetchAuthSession();
    const identityId = session.identityId;
    const idToken = session.tokens?.idToken;
    const token = idToken?.toString();
    const userData = await ProfileHandling.getUserDataFromToken(idToken);
    const userDataWithId = { ...userData, identityId };
    const userType = idToken?.payload["custom:userType"] as string;
    const userDomain = idToken?.payload["custom:domain"] as string;
    return { idToken, token, userData: userDataWithId, userType, userDomain };
  }

  async getDocumentDownloadUrl(documentId: string): Promise<string> {
    try {
      const { token, userData } = await this.getAuthInfo();

      const response = await client.queries.getDocumentDownloadUrl(
        {
          input: {
            documentId: documentId,
            userId: userData.identityId ?? "",
          },
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      if (response.errors) {
        throw new Error(
          `GraphQL Error: ${response.errors[0]?.message || "Unknown error"}`,
        );
      }

      if (!response.data) {
        throw new Error("No data returned from Lambda");
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async triggerDownload(downloadUrl: string, fileName: string): Promise<void> {
    const response = await fetch(downloadUrl);
    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async downloadDocument(documentId: string): Promise<void> {
    try {
      const { userData } = await this.getAuthInfo();
      const downloadUrl = await this.getDocumentDownloadUrl(documentId);
      const document = await this.getDocument(documentId, userData.identityId!);
      await this.triggerDownload(downloadUrl, document.fileName || "download");
    } catch (error) {
      console.error("Error downloading document", error);
      throw error;
    }
  }

  async downloadDocumentLink(documentId: string): Promise<void> {
    try {
      const downloadUrl = await this.getDocumentDownloadUrl(documentId);
      const document = await this.getDocumentLink(documentId);
      await this.triggerDownload(downloadUrl, document.fileName || "download");
    } catch (error) {
      console.error("Error downloading document link", error);
      throw error;
    }
  }

  async getUserDocuments(filters?: {
    documentTypes?: string[];
    status?: "ACTIVE" | "INACTIVE" | "DELETED";
    includeDeleted?: boolean;
  }): Promise<any[]> {
    try {
      const { userData, token } = await this.getAuthInfo();

      if (!userData.identityId) {
        throw new Error("User identity not found");
      }

      const data = await listAll((nextToken) =>
        client.models.Document.list({
          filter: {
            userId: { eq: userData.identityId },
          },
          authMode: "userPool",
          authToken: token,
          limit: 1000,
          ...(nextToken ? { nextToken } : {}),
        }),
      );

      let filteredDocs = (data || []).filter(
        (doc) => doc.isLatestVersion === true,
      );

      if (filters?.status) {
        filteredDocs = filteredDocs.filter(
          (doc) => doc.status === filters.status,
        );
      } else if (!filters?.includeDeleted) {
        filteredDocs = filteredDocs.filter((doc) => doc.status !== "DELETED");
      }

      if (filters?.documentTypes && filters.documentTypes.length > 0) {
        filteredDocs = filteredDocs.filter((doc) =>
          filters.documentTypes!.includes(doc.documentType || ""),
        );
      }

      filteredDocs.sort((a, b) => {
        const dateA = new Date(a.updatedAt || 0).getTime();
        const dateB = new Date(b.updatedAt || 0).getTime();
        return dateB - dateA;
      });

      const sanitizedDocs = filteredDocs.map((doc) =>
        DocumentMetadataSanitizer.sanitizeDocumentForDisplay(doc),
      );

      return sanitizedDocs;
    } catch (error) {
      throw error;
    }
  }

  async getDocumentsByTypes(documentTypes: string[]): Promise<any[]> {
    return this.getUserDocuments({ documentTypes });
  }

  async getDocumentsGroupedByType(
    documentTypes?: string[],
  ): Promise<{ [key: string]: any }> {
    try {
      const documents = await this.getUserDocuments({ documentTypes });

      const grouped: { [key: string]: any } = {};
      documents.forEach((doc) => {
        if (doc.documentType) {
          if (!grouped[doc.documentType]) {
            grouped[doc.documentType] = [];
          }
          grouped[doc.documentType].push(doc);
        }
      });

      return grouped;
    } catch (error) {
      throw error;
    }
  }

  async getDocumentByType(documentType: string): Promise<any | null> {
    try {
      const documents = await this.getDocumentsByTypes([documentType]);
      return documents.length > 0 ? documents[0] : null;
    } catch (error) {
      throw error;
    }
  }

  async removeDocument(documentId: string): Promise<any> {
    try {
      const { userData, token } = await this.getAuthInfo();

      if (!userData.identityId) {
        throw new Error("User identity not found");
      }

      const document = await this.getDocument(documentId, userData.identityId);

      if (document?.fileKey) {
        try {
          await remove({ path: document.fileKey });
        } catch (s3Error) {
          // Silently continue if file doesn't exist
        }
      }

      const data = await client.models.Document.delete(
        {
          userId: userData.identityId,
          documentId: documentId,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      return data;
    } catch (error) {
      throw error;
    }
  }

  async softDeleteDocument(documentId: string): Promise<any> {
    const { userData } = await this.getAuthInfo();

    if (!userData.identityId) {
      throw new Error("User identity not found");
    }

    return this.updateDocument(documentId, {
      status: "DELETED",
    });
  }

  async restoreDocument(documentId: string): Promise<any> {
    const { userData } = await this.getAuthInfo();

    if (!userData.identityId) {
      throw new Error("User identity not found");
    }

    return this.updateDocument(documentId, {
      status: "ACTIVE",
      deletedAt: undefined,
    });
  }

  async updateDocument(
    documentId: string,
    updates: {
      fileName?: string;
      documentType?: string;
      category?: string;
      description?: string;
      tags?: string[];
      expiryDate?: string;
      deletedAt?: string;
      status?: "ACTIVE" | "INACTIVE" | "DELETED";
    },
  ): Promise<any> {
    try {
      const { userData, token } = await this.getAuthInfo();

      if (!userData.identityId) {
        throw new Error("User identity not found");
      }

      const sanitizedUpdates =
        DocumentMetadataSanitizer.sanitizeDocumentUpdate(updates);

      const data = await client.models.Document.update(
        {
          userId: userData.identityId,
          documentId: documentId,
          ...sanitizedUpdates,
          documentType: sanitizedUpdates.documentType as any,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      return DocumentMetadataSanitizer.sanitizeDocumentForDisplay(data);
    } catch (error) {
      throw error;
    }
  }

  async getDocument(documentId: string, userId: string): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.Document.get(
        {
          userId: userId,
          documentId: documentId,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      if (errors) {
        throw new Error("Failed to fetch document");
      }

      return DocumentMetadataSanitizer.sanitizeDocumentForDisplay(data);
    } catch (error) {
      throw error;
    }
  }

  async getDocumentLink(id: string): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.DocumentLink.get(
        {
          id,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      if (errors) {
        throw new Error("Failed to fetch document link");
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async hasDocumentType(documentType: string): Promise<boolean> {
    try {
      const documents = await this.getDocumentsByTypes([documentType]);
      return documents.length > 0;
    } catch (error) {
      return false;
    }
  }

  async getDocumentCount(filters?: {
    documentTypes?: string[];
    status?: "ACTIVE" | "INACTIVE" | "DELETED";
  }): Promise<number> {
    try {
      const documents = await this.getUserDocuments(filters);
      return documents.length;
    } catch (error) {
      return 0;
    }
  }

  async getActiveDocuments(documentTypes?: string[]): Promise<any[]> {
    return this.getUserDocuments({
      documentTypes,
      status: "ACTIVE",
    });
  }

  async getDeletedDocuments(documentTypes?: string[]): Promise<any[]> {
    return this.getUserDocuments({
      documentTypes,
      status: "DELETED",
    });
  }

  async fetchDocumentsWithMapping(
    documentTypes: string[],
  ): Promise<
    Record<string, { documentId: string; userId: string; fileName: string }>
  > {
    try {
      const { userData, token } = await this.getAuthInfo();

      if (!userData.identityId) {
        throw new Error("User identity not found");
      }

      const documents = await listAll((nextToken) =>
        client.models.Document.list({
          filter: {
            userId: { eq: userData.identityId },
          },
          authMode: "userPool",
          authToken: token,
          limit: 1000,
          ...(nextToken ? { nextToken } : {}),
        }),
      );

      const filtered = documents.filter((doc) => {
        const matches =
          doc.status === "ACTIVE" &&
          doc.isLatestVersion === true &&
          doc.documentType &&
          documentTypes.includes(doc.documentType);

        return matches;
      });

      const uploaded: Record<
        string,
        { documentId: string; userId: string; fileName: string }
      > = {};

      filtered.forEach((doc) => {
        if (doc.documentType) {
          const key = doc.documentType
            .toLowerCase()
            .split("_")
            .map((word: string, index: number) =>
              index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1),
            )
            .join("");

          uploaded[key] = {
            documentId: doc.documentId,
            userId: doc.userId,
            fileName: doc.fileName || getDocumentTypeLabel(doc.documentType),
          };
        }
      });

      return uploaded;
    } catch (error) {
      console.error("[fetchDocumentsWithMapping] ERROR:", error);
      return {};
    }
  }

  countUploadedDocs(documentFields: Array<{ uploaded: boolean }>): number {
    return documentFields.filter((doc) => doc.uploaded).length;
  }

  countRequiredDocs(documentFields: Array<{ required: boolean }>): number {
    return documentFields.filter((doc) => doc.required).length;
  }

  private async linkDocuments(
    institutionId: string,
    entityId: string,
    entityType: "application" | "genuineStudent",
    documentTypes?: string[],
  ): Promise<void> {
    try {
      const { userType, userDomain, token, userData } =
        await this.getAuthInfo();

      if (!userData.identityId) {
        throw new Error("User identity not found");
      }

      let institutionDomain: string | undefined;
      let applicantId: string | null = null;

      if (userType === "Institution" || userType === "Agent") {
        institutionDomain = userDomain;
      } else {
        if (entityType === "application") {
          const { data: application } = await client.models.Application.get(
            { institutionId, applicationId: entityId },
            { authMode: "userPool", authToken: token },
          );
          if (!application) throw new Error("Application not found");
          institutionDomain = application.institutionId;
          applicantId = application.owner;
        } else {
          const { data: genuineStudent } =
            await client.models.GenuineStudent.get(
              { genuineStudentId: entityId },
              { authMode: "userPool", authToken: token },
            );
          if (!genuineStudent)
            throw new Error("Genuine Student record not found");

          if (genuineStudent.applicationId) {
            // Need to get institutionId from ApplicationAccess
            const accessRecords = await listAll((nextToken) =>
              client.models.ApplicationAccess.list({
                filter: { applicationId: { eq: genuineStudent.applicationId } },
                authMode: "userPool",
                authToken: token,
                ...(nextToken ? { nextToken } : {}),
              }),
            );

            if (accessRecords && accessRecords.length > 0) {
              const appInstitutionId = accessRecords[0].entityId;
              const { data: application } = await client.models.Application.get(
                {
                  institutionId: appInstitutionId,
                  applicationId: genuineStudent.applicationId,
                },
                { authMode: "userPool", authToken: token },
              );
              if (application) {
                institutionDomain = application.institutionId;
              }
            }
          }
        }
      }

      if (!institutionDomain) {
        throw new Error("Cannot link documents: No institution domain found");
      }

      const documentsMapping = await this.fetchDocumentsWithMapping(
        documentTypes || [],
      );

      const documentKeys = Object.values(documentsMapping);

      if (documentKeys.length === 0) {
        return;
      }

      const documents = await Promise.all(
        documentKeys.map(
          async (docKey: {
            documentId: string;
            userId: string;
            fileName: string;
          }) => {
            const { data } = await client.models.Document.get(
              { userId: docKey.userId, documentId: docKey.documentId },
              { authMode: "userPool", authToken: token },
            );
            return data;
          },
        ),
      );

      await Promise.all(
        documents
          .filter((doc: any) => doc)
          .map((doc: any) => {
            const linkData: any = {
              userId: doc!.userId,
              documentId: doc!.documentId,
              documentType: doc!.documentType,
              fileName: doc!.fileName || "",
              fileKey: doc!.fileKey || "",
              uploadedAt: doc!.uploadedAt || new Date().toISOString(),
              status: doc!.status || "ACTIVE",
              applicantId: applicantId,
              linkedAt: new Date().toISOString(),
              domain: institutionDomain!,
              institutionId: institutionDomain!,
            };

            if (entityType === "application") {
              linkData.applicationId = entityId;
            } else {
              linkData.genuineStudentId = entityId;
            }

            return client.models.DocumentLink.create(linkData, {
              authMode: "userPool",
              authToken: token,
            });
          }),
      );
    } catch (error) {
      throw error;
    }
  }

  async linkDocumentsToApplication(
    institutionId: string,
    applicationId: string,
    documentTypes?: string[],
  ): Promise<void> {
    return this.linkDocuments(
      institutionId,
      applicationId,
      "application",
      documentTypes,
    );
  }

  async linkDocumentsToGenuineStudent(
    institutionId: string,
    genuineStudentId: string,
    documentTypes?: string[],
  ): Promise<void> {
    return this.linkDocuments(
      institutionId,
      genuineStudentId,
      "genuineStudent",
      documentTypes,
    );
  }

  async getDocumentsByEntity(
    entityId: string,
    entityType: "application" | "genuineStudent",
  ): Promise<any[]> {
    try {
      const { token } = await this.getAuthInfo();

      const links = await listAll((nextToken) =>
        client.models.DocumentLink.list({
          filter:
            entityType === "application"
              ? { applicationId: { eq: entityId } }
              : { genuineStudentId: { eq: entityId } },
          authMode: "userPool",
          authToken: token,
          ...(nextToken ? { nextToken } : {}),
        }),
      );

      if (!links || links.length === 0) {
        return [];
      }

      const documentsWithLatestCheck = await Promise.all(
        links.map(async (link) => {
          try {
            if (!link.documentId) {
              return null;
            }

            const { data: doc } = await link.document();

            if (doc && doc.isLatestVersion === true) {
              return {
                id: link.documentId,
                documentId: link.documentId,
                documentType: link.documentType,
                fileName: link.fileName,
                uploadedAt: link.uploadedAt,
                status: link.status,
                linkedAt: link.linkedAt,
              };
            }
            return null;
          } catch (error) {
            console.error("Error fetching document:", error);
            return null;
          }
        }),
      );

      return documentsWithLatestCheck.filter((doc) => doc !== null) as any[];
    } catch (error) {
      throw error;
    }
  }

  async getDocumentsByApplicationId(applicationId: string): Promise<any[]> {
    return this.getDocumentsByEntity(applicationId, "application");
  }

  async getDocumentsByGenuineStudentId(
    genuineStudentId: string,
  ): Promise<any[]> {
    return this.getDocumentsByEntity(genuineStudentId, "genuineStudent");
  }

  async unlinkDocumentFromEntity(
    documentId: string,
    entityId: string,
    entityType: "application" | "genuineStudent",
  ): Promise<void> {
    try {
      const { token } = await this.getAuthInfo();

      const filterCondition =
        entityType === "application"
          ? {
              and: [
                { documentId: { eq: documentId } },
                { applicationId: { eq: entityId } },
              ],
            }
          : {
              and: [
                { documentId: { eq: documentId } },
                { genuineStudentId: { eq: entityId } },
              ],
            };

      const { data: links } = await client.models.DocumentLink.list({
        filter: filterCondition,
        authMode: "userPool",
        authToken: token,
      });

      if (!links || links.length === 0) return;

      await Promise.all(
        links.map((link) =>
          client.models.DocumentLink.delete(
            { id: link.id },
            { authMode: "userPool", authToken: token },
          ),
        ),
      );
    } catch (error) {
      throw error;
    }
  }

  async unlinkDocumentFromApplication(
    documentId: string,
    applicationId: string,
  ): Promise<void> {
    return this.unlinkDocumentFromEntity(
      documentId,
      applicationId,
      "application",
    );
  }

  async unlinkDocumentFromGenuineStudent(
    documentId: string,
    genuineStudentId: string,
  ): Promise<void> {
    return this.unlinkDocumentFromEntity(
      documentId,
      genuineStudentId,
      "genuineStudent",
    );
  }
}

export const documentService = new DocumentService();
